import type { PriceCalc, PriceCus, PriceGrp, PriceLine, PriceRoute, PriceSt, PriceSur, PriceUnit, PriceVer } from './priceTypes';

/** Ngày "hôm nay" của dữ liệu mẫu board. */
export const PRICE_TODAY = '2026-10-22';
export const PRICE_GROUPS: ReadonlyArray<{ k: PriceGrp; name: string; color: string }> = [
  { k: 'hq', name: 'Hải quan', color: 'var(--d-blue)' },
  { k: 'kd', name: 'Kiểm dịch / C/O', color: 'var(--d-green)' },
  { k: 'tr', name: 'Trucking', color: 'var(--route)' },
  { k: 'cg', name: 'Cảng / depot', color: 'var(--d-gray)' },
  { k: 'kh', name: 'Khác', color: 'var(--d-amber)' },
];
export const UNIT: Record<PriceUnit, string> = { lot: 'Lô', c20: 'Cont 20', c40: 'Cont 40', rf: 'Cont RF', dec: 'Tờ khai', trip: 'Chuyến' };
export const CALC: Record<PriceCalc, readonly [string, string]> = { fix: ['Cố định', 'Cố định'], act: ['Theo thực tế', 'Thực tế'], ahf: ['Thực tế + phí xử lý', 'TT + phí XL'] };
/** Trạng thái hiển thị: [nhãn, màu chữ, nền]. `exp` = còn hiệu lực nhưng sắp hết (≤ 30 ngày, chưa có bản kế). */
export type ShownSt = PriceSt | 'exp';
export const ST: Record<ShownSt, readonly [string, string, string]> = {
  on: ['Hiệu lực', 'var(--ok)', 'var(--ok-bg)'],
  exp: ['Sắp hết hiệu lực', 'var(--warn)', 'var(--warn-bg)'],
  wait: ['Chờ duyệt', 'var(--warn)', 'var(--warn-bg)'],
  sched: ['Sắp áp dụng', 'var(--link)', 'var(--pri-bg)'],
  off: ['Hết hiệu lực', 'var(--lb)', 'var(--sub)'],
  rej: ['Từ chối', 'var(--err)', 'var(--err-bg)'],
};
export const SEAT = { mgr: '[SAL-MGR] DAVID', bod: '[BOD-DIR] PHILIP' } as const;

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const dk = (s: string) => {
  if (!s) return '—';
  const p = s.split('-');
  return `${(p[2] ?? '').slice(0, 2)}/${MON[+(p[1] ?? 1) - 1]}`;
};
export const vnd = (x: number) => String(Math.round(x)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
const dayDiff = (a: string, b: string) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86400000);
const prevDay = (s: string) => new Date(Date.parse(`${s}T00:00:00Z`) - 86400000).toISOString().slice(0, 10);
const soon = (to: string) => !!to && to >= PRICE_TODAY && dayDiff(PRICE_TODAY, to) <= 30;

export interface VerView extends PriceVer { i: number; v: string }
export interface LineView extends Omit<PriceLine, 'vers'> { vers: VerView[]; cur: VerView; next: VerView | null; stx: ShownSt }

/** Điền ngày hết của bản cũ theo bản kế, tìm bản hiện hành (có hiệu lực, đã tới ngày), bản kế (chờ duyệt / sắp áp dụng) và trạng thái hiển thị. */
export function resolveLines(lines: PriceLine[]): LineView[] {
  return lines.map((n) => {
    const vers: VerView[] = n.vers.map((x, i) => ({ ...x, i, v: `v${i + 1}` }));
    vers.forEach((x, i) => {
      const nx = vers.slice(i + 1).find((w) => w.st === 'on' || w.st === 'sched');
      if (nx && !x.to && (x.st === 'on' || x.st === 'off')) x.to = prevDay(nx.from);
    });
    const cur = vers.filter((x) => x.st === 'on' && x.from <= PRICE_TODAY).slice(-1)[0] ?? vers[vers.length - 1]!;
    const next = vers.find((x) => x.i > cur.i && (x.st === 'wait' || x.st === 'sched')) ?? null;
    const stx: ShownSt = cur.st === 'on' && soon(cur.to) && !next ? 'exp' : cur.st;
    return { ...n, vers, cur, next, stx };
  });
}

export const priceText = (n: Pick<LineView, 'route' | 'calc'>, x: PriceVer) =>
  n.route ? 'Theo tuyến' : n.calc === 'act' ? 'Theo thực tế' : n.calc === 'ahf' ? `TT + ${vnd(x.std)}` : vnd(x.std);
export const floorText = (n: Pick<LineView, 'route' | 'calc'>, x: PriceVer) => (n.route || n.calc === 'act' || !x.flo ? '—' : vnd(x.flo));

export interface CusView extends PriceCus { ln: LineView; below: boolean; under: boolean; stx: ShownSt; seat: 'mgr' | 'bod' }
export function resolveCus(cus: PriceCus[], lines: LineView[]): CusView[] {
  const out: CusView[] = [];
  for (const c of cus) {
    const ln = lines.find((n) => n.id === c.line);
    if (!ln) continue;
    const below = c.price < ln.cur.std;
    const under = ln.cur.flo > 0 && c.price < ln.cur.flo;
    const gap = ln.cur.flo ? (ln.cur.flo - c.price) / ln.cur.flo : 0;
    out.push({ ...c, ln, below, under, seat: gap > 0.1 ? 'bod' : 'mgr', stx: c.st === 'on' && soon(c.to) ? 'exp' : c.st });
  }
  return out;
}

export type PriceFilter = 'all' | 'wait' | 'exp' | 'below';
export interface Scope { grp: PriceGrp | 'all'; code: string }
const inScope = (g: PriceGrp, code: string, s: Scope) => (s.grp === 'all' || g === s.grp) && (s.code === 'all' || code === s.code);

export function stdRows(lines: LineView[], s: Scope, qf: PriceFilter, all: boolean) {
  const keep = lines.filter((n) => inScope(n.grp, n.code, s) && (qf === 'all' || (qf === 'wait' && n.vers.some((x) => x.st === 'wait')) || (qf === 'exp' && n.stx === 'exp')));
  return keep.flatMap((n) => (all ? n.vers.slice().reverse().map((x) => ({ n, x, old: x.i !== n.cur.i })) : [{ n, x: n.cur, old: false }]));
}
export function cusRows(cus: CusView[], s: Scope, qf: PriceFilter) {
  return cus.filter((c) => inScope(c.ln.grp, c.ln.code, s) && (qf === 'all' || (qf === 'wait' && c.st === 'wait') || (qf === 'exp' && c.stx === 'exp') || (qf === 'below' && c.below)));
}
export const surRows = (sur: PriceSur[], s: Scope) => sur.filter((x) => inScope(x.grp, x.code, s));
export const routeRows = (r: PriceRoute[]) => r;

export function counts(lines: LineView[], cus: CusView[]) {
  return {
    wait: lines.filter((n) => n.vers.some((x) => x.st === 'wait')).length + cus.filter((c) => c.st === 'wait').length,
    exp: lines.filter((n) => n.stx === 'exp').length + cus.filter((c) => c.stx === 'exp').length,
    below: cus.filter((c) => c.below && c.st !== 'rej').length,
  };
}
/** Số dòng + số chờ duyệt cho cây dịch vụ. */
export function groupCount(lines: LineView[], cus: CusView[], k: PriceGrp | 'all') {
  return {
    n: lines.filter((n) => k === 'all' || n.grp === k).length,
    wait: lines.filter((n) => (k === 'all' || n.grp === k) && n.vers.some((x) => x.st === 'wait')).length + cus.filter((c) => (k === 'all' || c.ln.grp === k) && c.st === 'wait').length,
  };
}
export const codesOf = (lines: LineView[], k: PriceGrp) => [...new Set(lines.filter((n) => n.grp === k).map((n) => n.code))];
export function deltaText(c: CusView) {
  if (!c.ln.cur.std) return '—';
  const d = (c.price - c.ln.cur.std) / c.ln.cur.std;
  return `${d > 0 ? '+' : ''}${(d * 100).toFixed(1).replace('.', ',').replace(',0', '')}%`;
}

/** Tính thử phụ thu TRFA theo công thức của biểu (s1–s5). */
export function calcSur(i: { h: string; w: string; wh: string; ex: string }) {
  const num = (x: string) => +x.replace(/\./g, '').replace(',', '.') || 0;
  const h = num(i.h), w = num(i.w), wh = Math.max(0, Math.floor(num(i.wh))), ex = num(i.ex);
  const f = (x: number) => String(x).replace('.', ',');
  const mooc = h > 24 ? Math.ceil((h - 24) / 24) * 500000 : 0;
  const wait = w > 2 ? Math.ceil(w - 2) * 200000 : 0;
  const whs = wh * 800000;
  const exv = ex < 300000 ? 0 : ex;
  const rows: Array<[string, string, number]> = [
    ['Lưu mooc', h > 24 ? `⌈(${f(h)} − 24) / 24⌉ × 500.000` : '≤ 24h', mooc],
    ['Chờ', w > 2 ? `⌈${f(w)} − 2⌉ × 200.000` : '≤ 2h', wait],
    ['Kho thêm', `${wh} × 800.000`, whs],
    ['FPU · TRA · XLH', ex < 300000 ? `${vnd(ex)} < 300.000 → miễn` : vnd(ex), exv],
  ];
  return { rows, sum: mooc + wait + whs + exv };
}
